/**
 * Animated bottom-of-header activity strip.
 *
 * Idle  — silver hairline (matches the previous static strip — no visual
 *         regression when no query is running).
 * Active — a colored flash ignites from center, then a cyan→indigo→fuchsia
 *         gradient drifts horizontally while the LLM works. Edges feather
 *         to transparent on the left and right.
 *
 * Colors come from the shared AI_PALETTE — the same three icosahedron
 * normals used by the chat thinking dots and the dashboard's HeroShape, so
 * dots / 3D shape / header strip all glow the same palette in sync.
 *
 * Reduced motion: skips ignite scale-out, the flowing background-position,
 * and the breathing pulse — falls back to a static colored gradient that
 * only fades in/out.
 */

import { useChatActivity } from '@/contexts/ChatActivityContext';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import { AI_PALETTE } from './aiPalette';
import React, { useEffect, useRef, useState } from 'react';

const SILVER_HAIRLINE_BG =
  'linear-gradient(to right, transparent, rgba(192,192,192,0.25) 15%, rgba(192,192,192,0.35) 50%, rgba(192,192,192,0.25) 85%, transparent)';

const FLOW_GRADIENT = `linear-gradient(90deg, ${AI_PALETTE.cyan} 0%, ${AI_PALETTE.indigo} 25%, ${AI_PALETTE.fuchsia} 50%, ${AI_PALETTE.indigo} 75%, ${AI_PALETTE.cyan} 100%)`;

const EDGE_FEATHER_MASK =
  'linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)';

const IGNITE_GRADIENT = `linear-gradient(to right, transparent, ${AI_PALETTE.cyan} 35%, #ffffff 50%, ${AI_PALETTE.fuchsia} 65%, transparent)`;

const IGNITE_EASE = [0.16, 1, 0.3, 1] as [number, number, number, number];

// ---------------------------------------------------------------------------
// Subcomponents — each one a single motion element so AnimatePresence can
// track its enter / exit cleanly.
// ---------------------------------------------------------------------------

const SilverHairline: React.FC<{ dimmed: boolean }> = ({ dimmed }) => (
  <div
    aria-hidden
    className="absolute inset-0 transition-opacity duration-300"
    style={{
      background: SILVER_HAIRLINE_BG,
      boxShadow: '0 1px 6px 0 rgba(192,192,192,0.1)',
      opacity: dimmed ? 0.25 : 1,
    }}
  />
);

// While active, the flow layer is 3px tall — 1px taller than the 2px
// container — so the strip looks "thicker" without shifting page layout.
// The extra 1px overflows below the container, sitting above the content
// area on z-[5]. pointer-events-none keeps the overflow non-interactive.
const FlowingGradient: React.FC<{ animate: boolean }> = ({ animate }) => (
  <motion.div
    aria-hidden
    className="absolute top-0 left-0 right-0 h-[3px] pointer-events-none"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: animate ? 0.3 : 0.01, ease: 'easeOut' }}
    style={{
      background: FLOW_GRADIENT,
      backgroundSize: '200% 100%',
      WebkitMaskImage: EDGE_FEATHER_MASK,
      maskImage: EDGE_FEATHER_MASK,
      animation: animate
        ? 'streamFlow 6s linear infinite, activityBreathe 4s ease-in-out infinite'
        : 'none',
      filter: 'blur(0.4px)',
      willChange: 'background-position, opacity',
    }}
  />
);

const IgniteFlash: React.FC = () => (
  <motion.div
    aria-hidden
    className="absolute top-0 left-0 right-0 h-[3px] pointer-events-none"
    initial={{ scaleX: 0, opacity: 0.95 }}
    animate={{ scaleX: 1, opacity: 0 }}
    transition={{ duration: 0.45, ease: IGNITE_EASE }}
    style={{
      background: IGNITE_GRADIENT,
      transformOrigin: '50% 50%',
      WebkitMaskImage: EDGE_FEATHER_MASK,
      maskImage: EDGE_FEATHER_MASK,
      willChange: 'transform, opacity',
    }}
  />
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const HeaderActivityStrip: React.FC = () => {
  const { isActive } = useChatActivity();
  const shouldReduceMotion = useReducedMotion() ?? false;
  const animate = !shouldReduceMotion;

  // Bumps on every rising edge of isActive so the ignite flash remounts
  // (and replays) for every new query — including back-to-back queries
  // that abort each other before isActive ever falls.
  const [flashKey, setFlashKey] = useState(0);
  const prevActiveRef = useRef(isActive);

  useEffect(() => {
    if (isActive && !prevActiveRef.current) {
      setFlashKey((k) => k + 1);
    }
    prevActiveRef.current = isActive;
  }, [isActive]);

  return (
    <div
      className="h-[2px] relative z-[5]"
      role="presentation"
      data-chat-activity={isActive ? 'active' : 'idle'}
    >
      <SilverHairline dimmed={isActive} />

      <AnimatePresence>
        {isActive && <FlowingGradient key="flow" animate={animate} />}
      </AnimatePresence>

      {/* Ignite flash — animate-only and remount-keyed by flashKey. The
          motion animates opacity to 0 within 0.45s so it self-hides; no
          exit animation needed. flashKey > 0 guard avoids firing on the
          very first render when no query has fired yet. */}
      {animate && flashKey > 0 && <IgniteFlash key={flashKey} />}
    </div>
  );
};

export default HeaderActivityStrip;
